import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { PAGE_MEDIA, PAGE_MEDIA_DEFAULTS } from '@/lib/pageMediaCatalog';
import PageMediaUploader from '@/components/admin/PageMediaUploader';
import { ArrowLeft, Info, Image as ImageIcon } from 'lucide-react';

export default function AdminPageMedia() {
  const qc = useQueryClient();

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['page-media'],
    queryFn: () => base44.entities.PageMedia.list('-updated_date', 200),
  });

  const recordFor = (page, slotKey) =>
    records.find((r) => r.page_key === page && r.slot_key === slotKey);

  const refetch = () => qc.invalidateQueries({ queryKey: ['page-media'] });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-20 pb-20 px-6 lg:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Back link */}
        <Link
          to="/admin/dashboard"
          className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs font-bold tracking-widest mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
        </Link>

        {/* Header */}
        <p className="text-primary text-xs font-bold tracking-widest mb-2">MARKETING &amp; COMMS</p>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 bg-primary/15 border border-primary/30 flex items-center justify-center">
            <ImageIcon className="w-4 h-4 text-primary" />
          </div>
          <h1 className="text-white font-black text-2xl uppercase">Page Media</h1>
        </div>
        <p className="text-white/40 text-sm mb-8 max-w-2xl">
          Upload and replace the hero, banner and card images shown across the public site. Uploads take effect instantly.
        </p>

        {/* Best-practice guidance */}
        <div className="border border-primary/20 bg-primary/5 p-5 mb-10">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-primary" />
            <p className="text-white text-sm font-bold">Best practice — quality &amp; size</p>
          </div>
          <ul className="text-white/60 text-xs leading-relaxed space-y-1.5 list-disc pl-5">
            <li><span className="text-white">Format:</span> JPG for photos, PNG only when transparency is required.</li>
            <li><span className="text-white">Resize before uploading</span> to the recommended pixel dimensions for each slot — the browser shouldn't downscale a 4000 px photo.</li>
            <li><span className="text-white">Compress</span> to ~80% JPG quality. Aim for the file sizes shown on each slot; under 250 KB keeps pages fast.</li>
            <li><span className="text-white">Large homepage hero:</span> 1920×1080 is plenty — larger files just waste bandwidth and slow LCP.</li>
            <li><span className="text-white">Card images:</span> 800×600 is sharp on retina displays and weighs a fraction of a full-size export.</li>
          </ul>
        </div>

        {/* Per-page sections */}
        {Object.entries(PAGE_MEDIA).map(([pageKey, page]) => (
          <section key={pageKey} className="mb-12">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1.5 h-6 bg-primary" />
              <h2 className="text-white font-black text-lg uppercase tracking-wide">{page.label}</h2>
              <span className="text-white/30 text-xs font-mono">/{pageKey === 'home' ? '' : pageKey}</span>
            </div>

            {isLoading ? (
              <p className="text-white/30 text-sm">Loading…</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {page.slots.map((slot) => (
                  <PageMediaUploader
                    key={slot.key}
                    page={pageKey}
                    slot={slot}
                    record={recordFor(pageKey, slot.key)}
                    defaultUrl={PAGE_MEDIA_DEFAULTS[pageKey]?.[slot.key] || ''}
                    onSaved={refetch}
                  />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}